import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Card, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { RootState } from '../store';
import { getSlingLocations, useGetUsersId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { SlingLocation, SlingLocationsSummary } from '../api/generated/model';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import SlingLocationMappingEditorModal from '../components/SlingLocationMappingEditorModal';
import SlingLocationFilterBar, { SlingLocationFiltersState } from '../components/SlingLocationFilterBar';

// Import DataTable system
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';
import { renderSemibold } from '../components/common/DataTable';


type SlingLocationColumnKey =
  | 'id' | 'name' | 'state' | 'address' | 'src_id' | 'mapped_to' | 'shift_count' | 'shifts_without_events' | 'actions';

const SlingLocationsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  // Ref for scroll container
  const cardBodyRef = useRef<HTMLDivElement>(null);

  // Fetch user preferences
  const { data: userData } = useGetUsersId(user?.id || '', {
    query: {
      enabled: !!user?.id,
    }
  });

  // Sort state
  const [sortField, setSortField] = useState<SlingLocationColumnKey>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filter bar toggle state
  const [showFilterBar, setShowFilterBar] = useState(false);

  // Filters state
  const [filters, setFilters] = useState<SlingLocationFiltersState>({
    name: undefined,
  });

  // Fixed limit for infinite scroll
  const FETCH_LIMIT = 50;

  // Row identity. (src_id, id) is the composite primary key — shared with
  // the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback(
    (location: SlingLocation) => `${location.src_id}-${location.id}`,
    []
  );

  // Modal state
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<SlingLocation | null>(null);

  // Track modified locations for highlighting
  const [modifiedLocations, setModifiedLocations] = useState<Map<string, Set<string>>>(new Map());

  // Build query params - for cursor-based pagination, only send cursor + limit
  const buildQueryParams = useCallback(
    (cursor: string | undefined) =>
      cursor
        ? { cursor, limit: FETCH_LIMIT }
        : {
            sort_field: sortField as any,
            sort_direction: sortDirection,
            name: filters.name,
            limit: FETCH_LIMIT,
          },
    [sortField, sortDirection, filters]
  );

  const {
    allItems: locations,
    totalCount,
    apiSummary: locationsSummary,
    isLoading,
    error,
    hasMore,
    handleLoadMore,
    setAllItems: setAllLocations,
  } = usePaginatedQuery<SlingLocation, Awaited<ReturnType<typeof getSlingLocations>>, SlingLocationsSummary>({
    queryKey: 'sling-locations',
    buildQueryParams,
    queryFn: (params) => getSlingLocations(params as any),
    getRowKey,
  });

  // Clear highlighting when filters/sort change
  useEffect(() => {
    setModifiedLocations(new Map());
  }, [sortField, sortDirection, filters]);

  // Handle sorting
  const handleSortChange = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field as SlingLocationColumnKey);
    setSortDirection(direction);
  };

  // Handle mapping click
  const handleMappingClick = useCallback((location: SlingLocation) => {
    setSelectedLocation(location);
    setShowMappingModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowMappingModal(false);
    setSelectedLocation(null);
  }, []);

  // Handle mapping modification for highlighting
  const handleMappingModified = useCallback((
    locationKey: string,
    updatedMapping: { customer_id: string; customer_name?: string | null; location?: string | null; mapping_id: string }
  ) => {
    // Update the location in allLocations immediately
    setAllLocations(prev => {
      return prev.map(loc => {
        const key = `${loc.src_id}-${loc.id}`;
        if (key === locationKey) {
          // If customer_id is empty, this is a delete - clear the mapping
          if (!updatedMapping.customer_id) {
            return {
              ...loc,
              mapped_to_customer_id: null,
              mapped_to_customer_name: null,
              mapped_to_location: null,
              mapped_to_mapping_id: null,
            };
          }
          // Otherwise, update with new mapping
          return {
            ...loc,
            mapped_to_customer_id: updatedMapping.customer_id,
            mapped_to_customer_name: updatedMapping.customer_name || null,
            mapped_to_location: updatedMapping.location,
            mapped_to_mapping_id: updatedMapping.mapping_id,
          };
        }
        return loc;
      });
    });

    // Highlight the modified location
    setModifiedLocations(prev => {
      const newMap = new Map(prev);
      const locationFields = newMap.get(locationKey) || new Set<string>();
      locationFields.add('mapped_to');
      newMap.set(locationKey, locationFields);
      return newMap;
    });
  }, []);

  // Filter handlers
  const handleFiltersChange = useCallback((newFilters: SlingLocationFiltersState) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      name: undefined,
    });
  }, []);

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(value => value).length;

  // Define columns
  const columns: ColumnConfig<SlingLocation>[] = useMemo(() => [
    {
      key: 'id',
      label: 'Location ID',
      sortable: true,
      width: 180,
      renderCell: (location) => renderSemibold(location.id?.toString()),
      tooltip: (location) => location.id?.toString() || '',
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: 300,
      renderCell: (location) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {renderSemibold(location.name)}
        </div>
      ),
      tooltip: (location) => location.name || '',
    },
    {
      key: 'state',
      label: 'State',
      sortable: true,
      width: 120,
      renderCell: (location) => location.state || null,
      tooltip: (location) => location.state || '',
    },
    {
      key: 'address',
      label: 'Address',
      sortable: true,
      width: 350,
      renderCell: (location) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {location.address || null}
        </div>
      ),
      tooltip: (location) => location.address || '',
    },
    {
      key: 'src_id',
      label: 'Source ID',
      sortable: true,
      width: 200,
      renderCell: (location) => (
        <span className="font-monospace small text-muted">
          {location.src_id}
        </span>
      ),
      tooltip: (location) => location.src_id || '',
    },
    {
      key: 'mapped_to',
      label: 'Mapped To',
      sortable: true,
      width: 400,
      renderCell: (location) => {
        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          handleMappingClick(location);
        };

        if (location.mapped_to_customer_name) {
          const locationStr = location.mapped_to_location || 'Primary/Default';
          return (
            <Button
              variant="success"
              size="sm"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%'
              }}
              onClick={handleClick}
            >
              <i className="bi bi-pencil me-1"></i>
              {location.mapped_to_customer_name} - {locationStr}
            </Button>
          );
        }
        return (
          <Button
            variant="warning"
            size="sm"
            onClick={handleClick}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Create Mapping
          </Button>
        );
      },
      tooltip: (location) => {
        if (location.mapped_to_customer_name) {
          const locationStr = location.mapped_to_location || 'Primary/Default';
          return `Click to edit: ${location.mapped_to_customer_name} - ${locationStr}`;
        }
        return 'Click to create mapping';
      },
    },
    {
      key: 'shift_count',
      label: 'Number of Shifts',
      sortable: true,
      width: 180,
      renderCell: (location) => {
        const count = location.shift_count || 0;
        return (
          <span className={`badge ${count > 0 ? 'bg-primary' : 'bg-secondary'}`}>
            {count}
          </span>
        );
      },
      tooltip: (location) => {
        const count = location.shift_count || 0;
        return `${count} shifts`;
      },
    },
    {
      key: 'shifts_without_events',
      label: 'Shifts w/o Events',
      sortable: true,
      width: 180,
      renderCell: (location) => {
        const count = location.shifts_without_events || 0;
        const total = location.shift_count || 0;
        const hasUnassociated = count > 0;

        return (
          <div className="d-flex align-items-center gap-2">
            <span className={`badge ${hasUnassociated ? 'bg-warning' : 'bg-success'}`}>
              {count}
            </span>
            {hasUnassociated && total > 0 && (
              <small className="text-muted">/ {total}</small>
            )}
          </div>
        );
      },
      tooltip: (location) => {
        const count = location.shifts_without_events || 0;
        const total = location.shift_count || 0;
        return `${count} of ${total} shifts not associated with events`;
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      width: 250,
      renderCell: (location) => {
        return (
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/sling/locations/${location.src_id}/${location.id}`);
              }}
            >
              Open Detail
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`https://app.getsling.com/manage/locations/${location.id}`, '_blank', 'noopener,noreferrer');
              }}
            >
              Open in Sling
            </Button>
          </div>
        );
      },
      tooltip: (location) => 'View location details or open in Sling',
    },
  ], [handleMappingClick, navigate]);

  // Calculate summary stats
  const displayTotal = totalCount ?? locations.length;

  // Summary card content
  const stickyHeaderContent = (
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <Card className="">
        <Card.Body className="text-center p-2">
          <div className="mb-2">
            <i className="bi bi-geo-alt fs-1 text-primary"></i>
          </div>
          <h3 className="mb-1">{displayTotal}</h3>
          <p className="text-muted mb-0 small">Total Locations</p>
        </Card.Body>
      </Card>
      <Card className="">
        <Card.Body className="text-center p-2">
          <div className="mb-2">
            <i className="bi bi-exclamation-triangle fs-1 text-warning"></i>
          </div>
          <h3 className="mb-1">{locationsSummary?.events_without_sling_location ?? 0}</h3>
          <p className="text-muted mb-0 small">Events Without Mapping</p>
        </Card.Body>
      </Card>
      <Card className="">
        <Card.Body className="text-center p-2">
          <div className="mb-2">
            <i className="bi bi-calendar-x fs-1 text-warning"></i>
          </div>
          <h3 className="mb-1">{locationsSummary?.total_shifts_without_events ?? 0}</h3>
          <p className="text-muted mb-0 small">Shifts w/o Events</p>
        </Card.Body>
      </Card>
    </div>
  );

  return (
    <DashboardLayout
      title="Sling Locations"
      subtitle="View and manage all Sling locations"
      stickyHeader={stickyHeaderContent}
      scrollable
    >
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
            <Card.Header className="bg-primary text-white flex-shrink-0">
              <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                    <span className="text-primary fw-semibold">
                      <i className="bi bi-geo-alt me-1"></i>
                      Sling Locations
                    </span>
                    <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                    <Button
                      variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
                      size="sm"
                      onClick={() => setShowFilterBar(!showFilterBar)}
                    >
                      <i className="bi bi-funnel me-1"></i>
                      Filters
                      {activeFilterCount > 0 && (
                        <span className="badge bg-header-control text-primary ms-1">{activeFilterCount}</span>
                      )}
                    </Button>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="bg-header-control rounded px-3 py-1">
                    <span className="text-primary">
                      {displayTotal > 0 ? (
                        <>{displayTotal} total locations</>
                      ) : (
                        'No locations found'
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {showFilterBar && (
                <SlingLocationFilterBar
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onClearFilters={handleClearFilters}
                />
              )}
            </Card.Header>
            <Card.Body
              ref={cardBodyRef}
              className="flex-grow-1 overflow-hidden"
              style={{
                padding: 0,
                minHeight: 0,
              }}
            >
              {error ? (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center text-muted">
                    <i className="bi bi-exclamation-triangle fs-1 mb-3 d-block text-danger"></i>
                    <h5>Failed to Load</h5>
                    <p className="mb-0">{(error as any)?.response?.data?.message || (error as any)?.message || 'Failed to load Sling locations'}</p>
                  </div>
                </div>
              ) : (
                <DataTable
                  data={locations}
                  columns={columns}
                  getRowKey={getRowKey}
                  sortConfig={{ field: sortField, direction: sortDirection }}
                  onSortChange={handleSortChange}
                  highlightedRows={new Set(modifiedLocations.keys())}
                  modifiedCells={modifiedLocations}
                  pagination={{
                    hasMore,
                    limit: FETCH_LIMIT,
                  }}
                  onLoadMore={handleLoadMore}
                  isLoading={isLoading}
                  preferencesKey="sling-locations"
                  userId={user?.id}
                  userPreferences={userData?.preferences}
                  maxHeight="100%"
                  enableColumnReorder={true}
                  enableColumnResize={true}
                  enableColumnVisibility={true}
                  enableStickyHeader={true}
                />
              )}
            </Card.Body>
      </Card>

      {/* Mapping Editor Modal */}
      <SlingLocationMappingEditorModal
        show={showMappingModal}
        onHide={handleCloseModal}
        slingLocation={selectedLocation}
        onMappingModified={handleMappingModified}
      />
    </DashboardLayout>
  );
};

export default SlingLocationsPage;
